import { iconOverrides } from "ag-grid-community";

import arrowUpIcon from "@user/assets/images/skyds_grid_icons/icons/arrow-up.svg?raw";
import filterIcon from "@user/assets/images/skyds_grid_icons/icons/filter.svg?raw";
import filterPlusIcon from "@user/assets/images/skyds_grid_icons/icons/filter-plus.svg?raw";
import doubleChevronFirstIcon from "@user/assets/images/skyds_grid_icons/icons/double-chevron-first.svg?raw";
import gripIcon from "@user/assets/images/skyds_grid_icons/icons/grip.svg?raw";
import closeSquareIcon from "@user/assets/images/skyds_grid_icons/icons/close-square.svg?raw";
import documentStatusIcon from "@user/assets/images/skyds_grid_icons/icons/document-status.svg?raw";
import chevronRightIcon from "@user/assets/images/skyds_grid_icons/icons/chevron-right.svg?raw";
import noViewerIcon from "@user/assets/images/skyds_grid_icons/icons/no-viewer.svg?raw";
import pinIcon from "@user/assets/images/skyds_grid_icons/icons/pin.svg?raw";
import sigmaIcon from "@user/assets/images/skyds_grid_icons/icons/sigma.svg?raw";
import dragIcon from "@user/assets/images/skyds_grid_icons/icons/drag.svg?raw";
import arrowRightIcon from "@user/assets/images/skyds_grid_icons/icons/arrow-right.svg?raw";
import disabledIcon from "@user/assets/images/skyds_grid_icons/icons/disabled.svg?raw";
import chevronDownIcon from "@user/assets/images/skyds_grid_icons/icons/chevron-down.svg?raw";
import gridTableIcon from "@user/assets/images/skyds_grid_icons/icons/grid-table.svg?raw";
import copyIcon from "@user/assets/images/skyds_grid_icons/icons/copy.svg?raw";
import closeIcon from "@user/assets/images/skyds_grid_icons/icons/close.svg?raw";
import fileCsvIcon from "@user/assets/images/skyds_grid_icons/icons/file-csv.svg?raw";
import cutIcon from "@user/assets/images/skyds_grid_icons/icons/cut.svg?raw";
import writeIcon from "@user/assets/images/skyds_grid_icons/icons/write.svg?raw";
import fileExcelIcon from "@user/assets/images/skyds_grid_icons/icons/file-excel.svg?raw";
import rowGroupIcon from "@user/assets/images/skyds_grid_icons/icons/row-group.svg?raw";
import linkedIcon from "@user/assets/images/skyds_grid_icons/icons/linked.svg?raw";
import unLinkedIcon from "@user/assets/images/skyds_grid_icons/icons/unlinked.svg?raw";
import loadingIcon from "@user/assets/images/skyds_grid_icons/icons/loading.svg?raw";
import zoomInIcon from "@user/assets/images/skyds_grid_icons/icons/zoom-in.svg?raw";
import zoomOutIcon from "@user/assets/images/skyds_grid_icons/icons/zoom-out.svg?raw";
import moreVerticalIcon from "@user/assets/images/skyds_grid_icons/icons/more-vertical.svg?raw";
import menuIcon from "@user/assets/images/skyds_grid_icons/icons/menu.svg?raw";
import entryProhibitedIcon from "@user/assets/images/skyds_grid_icons/icons/entry-prohibited.svg?raw";
import chevronUpdownIcon from "@user/assets/images/skyds_grid_icons/icons/chevron-updown.svg?raw";
import documentSendIcon from "@user/assets/images/skyds_grid_icons/icons/document-send.svg?raw";
import downloadIcon from "@user/assets/images/skyds_grid_icons/icons/download.svg?raw";
import rowsIcon from "@user/assets/images/skyds_grid_icons/icons/rows.svg?raw";
import otherPlusIcon from "@user/assets/images/skyds_grid_icons/icons/other-plus.svg?raw";
import checkIcon from "@user/assets/images/skyds_grid_icons/icons/check.svg?raw";
import minusIcon from "@user/assets/images/skyds_grid_icons/icons/minus.svg?raw";

/**
 * SVG 문자열의 fill/stroke 색상을 원하는 색으로 치환합니다.
 * 기본값은 currentColor로, 그리드 테마의 iconButtonColor / iconButtonActiveColor를
 * 그대로 물려받도록 합니다.
 *
 * @param svg 원본 SVG 마크업 문자열
 * @param color 적용할 색상 (기본값: "currentColor")
 */
function tintSvg(svg: string, color: string = "currentColor"): string {
  let result = svg;

  // 1) fill="none"은 의도된 투명 처리(구멍 뚫린 도형 등)이므로 보존
  //    임시 플레이스홀더로 치환해 아래 전역 치환에서 제외
  const NONE_PLACEHOLDER = "__FILL_NONE__";
  result = result.replace(/fill="none"/gi, `fill="${NONE_PLACEHOLDER}"`);

  // 2) 기존 fill/stroke 속성값을 모두 원하는 색으로 치환 (전역, 대소문자 무시)
  result = result.replace(/fill="[^"]*"/gi, `fill="${color}"`);
  result = result.replace(/stroke="[^"]*"/gi, `stroke="${color}"`);

  // 3) fill 속성이 전혀 없는 SVG라면 브라우저 기본값(검정)이 적용되므로
  //    <svg> 루트 태그에 fill 속성을 새로 추가
  if (!/fill=/i.test(svg)) {
    result = result.replace(/<svg(\s|>)/i, `<svg fill="${color}"$1`);
  }

  // 4) 플레이스홀더 원복 — fill="none"은 그대로 유지
  result = result.replace(new RegExp(`fill="${NONE_PLACEHOLDER}"`, "g"), 'fill="none"');

  return result;
}

function rotateSvg(svg: string, degrees: number): string {
  // viewBox 중심 좌표를 기준으로 회전 (기본 20x20 가정, 필요시 파싱해서 동적으로 계산)
  const viewBoxMatch = svg.match(/viewBox="0 0 (\d+) (\d+)"/i);
  const w = viewBoxMatch ? Number(viewBoxMatch[1]) : 20;
  const h = viewBoxMatch ? Number(viewBoxMatch[2]) : 20;
  const cx = w / 2;
  const cy = h / 2;

  // 기존 path/g를 <g transform="..."> 로 감싸기
  return svg.replace(
    /(<svg[^>]*>)([\s\S]*)(<\/svg>)/i,
    `$1<g transform="rotate(${degrees} ${cx} ${cy})">$2</g>$3`
  );
}

export const skydsGridIcons = iconOverrides({
  type: "image",
  mask: true,
  icons: {
    // 1. 정렬
    asc: { svg: tintSvg(arrowUpIcon) },
    desc: { svg: rotateSvg(tintSvg(arrowUpIcon), 180) },
    sortUnSort: { svg: tintSvg(chevronUpdownIcon) },

    // 2. 필터
    filter: { svg: tintSvg(filterIcon) },
    filterAdd: { svg: tintSvg(filterPlusIcon) },
    filterCardExpand: { svg: tintSvg(chevronDownIcon) },
    filterCardEditing: { svg: tintSvg(writeIcon) },

    // 3. 트리 및 행 그룹화
    treeClosed: { svg: tintSvg(chevronRightIcon) },
    treeOpened: { svg: rotateSvg(tintSvg(chevronRightIcon), 180) },
    rowGroupPanel: { svg: tintSvg(rowGroupIcon) },
    pivotPanel: { svg: tintSvg(rowsIcon) },

    // 4. 페이지네이션
    first: { svg: tintSvg(doubleChevronFirstIcon) },
    last: { svg: rotateSvg(tintSvg(doubleChevronFirstIcon), 180) },
    next: { svg: tintSvg(chevronRightIcon) },
    previous: { svg: rotateSvg(tintSvg(chevronRightIcon), 180) },

    // 5. 기본 유틸리티 및 창 제어
    menu: { svg: tintSvg(menuIcon) },
    menuAlt: { svg: tintSvg(moreVerticalIcon) },
    close: { svg: tintSvg(closeIcon) },
    cancel: { svg: tintSvg(closeSquareIcon) },
    check: { svg: tintSvg(checkIcon) },
    minus: { svg: tintSvg(minusIcon) },

    // 6. 드래그 및 상태 관리
    rowDrag: { svg: tintSvg(gripIcon) },
    columnDrag: { svg: tintSvg(gripIcon) },
    gripLine: { svg: tintSvg(gripIcon) },
    loading: { svg: tintSvg(loadingIcon) },
    "not-allowed": { svg: tintSvg(disabledIcon) },

    // 7. 사이드바 및 액션 파일
    columns: { svg: tintSvg(gridTableIcon) },
    pin: { svg: tintSvg(pinIcon) },
    pivot: { svg: tintSvg(rowsIcon) },
    csvExport: { svg: tintSvg(fileCsvIcon) },
    excelExport: { svg: tintSvg(fileExcelIcon) },
    clipboardCopy: { svg: tintSvg(copyIcon) },
    clipboardCut: { svg: tintSvg(cutIcon) },
    clipboardPaste: { svg: tintSvg(documentSendIcon) },
    save: { svg: tintSvg(downloadIcon) },

    // 8. 기타 확장 기능
    maximize: { svg: tintSvg(zoomInIcon) },
    minimize: { svg: tintSvg(zoomOutIcon) },
    chart: { svg: tintSvg(documentStatusIcon) },
    linked: { svg: tintSvg(linkedIcon) },
    unlinked: { svg: tintSvg(unLinkedIcon) },
  }
});
